import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Wrench, Stethoscope, Settings, Car } from 'lucide-react';
import SEO from '../components/SEO';

const PartnersPage = () => {
  const services = [
    {
      icon: Wrench,
      title: 'Mécanique',
      description: 'Entretien, service rapide, réparations mécaniques'
    },
    {
      icon: Stethoscope,
      title: 'Diagnostic',
      description: 'Lecture code défauts, recherche de panne, mise à jour logiciel'
    },
    {
      icon: Settings,
      title: 'Boîte de vitesses',
      description: 'Entretien et réparations boîte de vitesses'
    },
    {
      icon: Car,
      title: 'Carrosserie',
      description: 'Réparations, peinture, débosselage'
    }
  ];

  const futureLocations = [
    {
      title: 'Genève Rive Gauche',
      timing: 'Courant 2027',
      flag: '🇨🇭'
    },
    {
      title: 'Vaud',
      timing: 'Fin 2027',
      flag: '🇨🇭'
    }
  ];

  return (
    <div className="pt-20">
      <SEO
        title="Nos garages — CarMaintenance en Suisse romande"
        description="Découvrez les garages CarMaintenance, à commencer par notre premier garage à Meyrin (Genève). Mécanique, carrosserie, diagnostic et entretien complet."
        path="/nos-partenaires"
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-bottom duration-700">
            Nos garages
          </h1>
        </div>
      </section>

      {/* Main Garage Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-8 lg:p-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                CarMaintenance <span className="text-[#52a675]">Meyrin</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Grâce à vous, nous avons ouvert notre premier garage sur Genève.
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 text-lg">Horaires d'ouverture</h3>
                  <div className="space-y-2 text-gray-600">
                    <p><strong>Lundi au Vendredi :</strong> 08:00 – 12:00 et 13:30 – 18:00</p>
                    <p><strong>Samedi :</strong> Fermé</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4 text-lg">Localisation</h3>
                  <div className="rounded-xl h-48 overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2759.1!2d6.0807!3d46.2356!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c653c4e6e0b39%3A0xabc123def456!2sRue%20des%20Lattes%2062%2C%201217%20Meyrin!5e0!3m2!1sfr!2sch!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      title="CarMaintenance Meyrin"
                    ></iframe>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Link to="/garage-meyrin" className="flex-1">
                  <Button
                    size="lg"
                    className="w-full bg-[#52a675] hover:bg-[#3d8a5c] text-white group"
                  >
                    En voir plus
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Services proposés par <span className="text-[#52a675]">CarMaintenance Meyrin</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez quelques-uns des services offerts par notre garage partenaire
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-16 h-16 bg-[#52a675]/10 rounded-full flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-[#52a675]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/garage-meyrin">
              <Button size="lg" className="bg-[#52a675] hover:bg-[#3d8a5c] text-white group">
                Voir tous les services
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Future Locations Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#52a675] uppercase tracking-wide">Contactez-nous</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">
              Trouvez-nous sur ces <span className="text-[#52a675]">locations</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              N'hésitez pas à nous contacter en utilisant les options ci-dessous, et notre équipe dédiée répondra rapidement à vos demandes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {futureLocations.map((location, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-4xl mb-4">{location.flag}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{location.title}</h3>
                <p className="text-[#52a675] font-semibold text-lg">{location.timing}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] to-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Une question ? N'hésitez pas à nous contacter.
          </h2>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              Contactez-nous
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
